import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { chimneyService } from '../services/chimneyService';
import { appointmentService } from '../services/appointmentService';
import { reviewService } from '../services/reviewService';
import { messageService } from '../services/messageService';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const ChimneyProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [chimney, setChimney] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [phoneVisible, setPhoneVisible] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [chimneyData, reviewsData] = await Promise.all([
          chimneyService.getById(id),
          reviewService.getByChimney(id),
        ]);
        setChimney(chimneyData);
        setReviews(reviewsData.results || reviewsData || []);
      } catch (err) {
        setError('Nie udało się załadować profilu kominiarza');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleAppointment = async (e) => {
    e.preventDefault();

    if (!user) {
      alert('Musisz być zalogowany, aby umówić wizytę');
      navigate('/login');
      return;
    }

    if (!appointmentDate || !appointmentTime) {
      alert('Proszę wybrać datę i godzinę wizyty');
      return;
    }

    setSubmitting(true);
    try {
      const datetime = `${appointmentDate}T${appointmentTime}:00`;
      await appointmentService.create({
        chimney: id,
        appointment_date: datetime,
        notes: appointmentNotes.trim() || undefined,
      });
      alert('Wizyta została umówiona!');
      navigate('/appointments');
    } catch (err) {
      alert(
        err.response?.data?.detail ||
          'Nie udało się umówić wizyty. Spróbuj ponownie.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleMessage = async () => {
    if (!user) {
      alert('Musisz być zalogowany, aby wysłać wiadomość');
      navigate('/login');
      return;
    }

    try {
      const response = await messageService.createRoom(id);
      navigate(`/chat/${response.id}`);
    } catch (err) {
      console.error('Błąd tworzenia pokoju:', err);
      alert('Nie udało się utworzyć pokoju rozmowy');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="ik-container py-16">
          <p className="text-center font-display text-lg text-ember-600">Ładowanie…</p>
        </div>
      </Layout>
    );
  }

  if (error || !chimney) {
    return (
      <Layout>
        <div className="ik-container py-16">
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-800">
            {error || 'Kominiarz nie został znaleziony'}
          </div>
          <Link to="/chimneys" className="mt-4 inline-block text-sm font-medium text-ember-600 hover:text-ember-700">
            ← Powrót do listy
          </Link>
        </div>
      </Layout>
    );
  }

  const averageRating =
    reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : 0;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: chimney.company_name || 'Kominiarz',
    description: chimney.description || 'Profesjonalne usługi kominiarskie',
    address: {
      '@type': 'PostalAddress',
      addressLocality: chimney.city || '',
      addressCountry: 'PL',
    },
    telephone: chimney.phone || '',
    email: chimney.email || '',
    url: `https://ikominiarz.pl/chimney/${chimney.id}`,
    aggregateRating:
      reviews.length > 0
        ? {
            '@type': 'AggregateRating',
            ratingValue: averageRating,
            reviewCount: reviews.length,
          }
        : undefined,
  };

  const inputClass =
    'w-full rounded-md border border-soot-300 bg-white px-3 py-2.5 text-soot-900 placeholder:text-soot-400 focus:border-ember-500 focus:outline-none focus:ring-2 focus:ring-ember-500/30';

  return (
    <Layout>
      <SEO
        title={`${chimney.company_name || 'Kominiarz'} - ${chimney.city || ''} | iKominiarz.pl`}
        description={
          chimney.description ||
          `Profesjonalne usługi kominiarskie w ${chimney.city || 'Twojej okolicy'}. ${chimney.company_name || 'Kominiarz'} - sprawdzone usługi, opinie klientów.`
        }
        keywords={`kominiarz ${chimney.city || ''}, usługi kominiarskie ${chimney.city || ''}, ${chimney.company_name || 'kominiarz'}`}
        url={`https://ikominiarz.pl/chimney/${chimney.id}`}
        structuredData={structuredData}
      />

      {/* Service header */}
      <section className="border-b border-soot-200 bg-soot-900 text-white">
        <div className="ik-container py-10 md:py-14">
          <Link
            to="/chimneys"
            className="text-sm text-soot-400 transition hover:text-white"
          >
            ← Wszyscy kominiarze
          </Link>
          <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_320px] lg:items-end">
            <div>
              <p className="text-sm font-medium uppercase tracking-wider text-ember-500">
                Usługi kominiarskie
                {chimney.city ? ` · ${chimney.city}` : ''}
              </p>
              <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight md:text-4xl">
                {chimney.company_name || 'Kominiarz'}
              </h1>
              {averageRating > 0 && (
                <p className="mt-3 text-soot-300">
                  <span className="font-semibold text-white">{averageRating}</span>
                  <span className="mx-1.5 text-ember-500">★★★★★</span>
                  {reviews.length}{' '}
                  {reviews.length === 1 ? 'opinia' : reviews.length < 5 ? 'opinie' : 'opinii'}
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-soot-300">
                {chimney.address && <span>{chimney.address}</span>}
                {chimney.postal_code && chimney.city && (
                  <span>
                    {chimney.postal_code} {chimney.city}
                  </span>
                )}
                {chimney.phone && (
                  <button
                    type="button"
                    onClick={() => setPhoneVisible(true)}
                    className="font-medium text-ember-500 hover:text-ember-100"
                  >
                    {phoneVisible ? chimney.phone : 'Pokaż telefon'}
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              {user ? (
                <button type="button" onClick={handleMessage} className="ik-btn-secondary !border-soot-600 !bg-transparent !text-white hover:!border-white hover:!bg-white/10">
                  Napisz wiadomość
                </button>
              ) : (
                <Link to="/login" className="ik-btn-secondary !border-soot-600 !bg-transparent !text-center !text-white hover:!border-white hover:!bg-white/10">
                  Zaloguj, aby pisać
                </Link>
              )}
              <a href="#booking" className="ik-btn-primary text-center">
                Umów wizytę
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="ik-container py-10 md:py-14">
        <div className="grid gap-12 lg:grid-cols-[1fr_360px] lg:items-start">
          <div className="space-y-12">
            {chimney.description && (
              <section>
                <h2 className="font-display text-xl font-semibold text-soot-950">O usługach</h2>
                <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-soot-600">
                  {chimney.description}
                </p>
              </section>
            )}

            <section>
              <h2 className="font-display text-xl font-semibold text-soot-950">
                Opinie{reviews.length > 0 ? ` (${reviews.length})` : ''}
              </h2>
              {reviews.length === 0 ? (
                <p className="mt-4 text-soot-500">Brak opinii — bądź pierwszą osobą po wizycie.</p>
              ) : (
                <ul className="mt-6 divide-y divide-soot-200 border-t border-soot-200">
                  {reviews.map((review) => (
                    <li key={review.id} className="py-5">
                      <div className="flex items-center gap-3">
                        <span className="text-ember-600">
                          {'★'.repeat(review.rating)}
                          {'☆'.repeat(5 - review.rating)}
                        </span>
                        <span className="text-sm text-soot-500">
                          {review.user?.first_name || 'Użytkownik'}
                        </span>
                      </div>
                      {review.comment && (
                        <p className="mt-2 text-soot-700">{review.comment}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* Booking panel – interaction card OK */}
          <aside
            id="booking"
            className="scroll-mt-24 rounded-md border border-soot-200 bg-white p-6 shadow-soft lg:sticky lg:top-24"
          >
            <h2 className="font-display text-xl font-semibold text-soot-950">Umów wizytę</h2>
            <p className="mt-2 text-sm text-soot-500">
              Wybierz termin przeglądu. Potwierdzenie i przypomnienia dostaniesz na konto.
            </p>

            {user ? (
              <form onSubmit={handleAppointment} className="mt-6 space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-soot-700" htmlFor="appt-date">
                    Data
                  </label>
                  <input
                    id="appt-date"
                    type="date"
                    required
                    value={appointmentDate}
                    onChange={(e) => setAppointmentDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-soot-700" htmlFor="appt-time">
                    Godzina
                  </label>
                  <input
                    id="appt-time"
                    type="time"
                    required
                    value={appointmentTime}
                    onChange={(e) => setAppointmentTime(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-soot-700" htmlFor="appt-notes">
                    Dodatkowe informacje
                  </label>
                  <textarea
                    id="appt-notes"
                    rows="3"
                    value={appointmentNotes}
                    onChange={(e) => setAppointmentNotes(e.target.value)}
                    placeholder="Np. rodzaj usługi, uwagi, dostęp do budynku"
                    className={inputClass}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="ik-btn-primary w-full disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? 'Umawianie…' : 'Umów wizytę'}
                </button>
              </form>
            ) : (
              <div className="mt-6 space-y-4">
                <p className="text-sm text-soot-600">
                  Zaloguj się lub załóż konto, aby umówić przegląd.
                </p>
                <Link to="/login" className="ik-btn-primary block w-full text-center">
                  Zaloguj się
                </Link>
                <Link to="/register" className="ik-btn-secondary block w-full text-center">
                  Zarejestruj się
                </Link>
              </div>
            )}

            {chimney.email && (
              <p className="mt-6 border-t border-soot-100 pt-4 text-xs text-soot-400">
                Kontakt: {chimney.email}
              </p>
            )}
          </aside>
        </div>
      </div>
    </Layout>
  );
};

export default ChimneyProfile;
